import { useState, useEffect } from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import { applyStyles } from '../../utils/styleUtils';

const LoadingBar = ({ barItem, onComplete, isAnimating, isCompleted }) => {
    const { replaceTemplates } = useOnboarding();
    const [percentage, setPercentage] = useState(0);
    const [barWidth, setBarWidth] = useState(0);

    useEffect(() => {
        if (isAnimating) {
            const duration = barItem.animationDuration || 2000;
            const startTime = Date.now();

            // Start bar animation
            setBarWidth(100);

            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const currentPercent = Math.floor(progress * 100);

                setPercentage(currentPercent);

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    setTimeout(onComplete, 200);
                }
            };

            animate();
        }
    }, [isAnimating, barItem.animationDuration, onComplete]);

    const barStyle = {
        marginTop: typeof barItem['margin-top'] === 'number'
            ? `${barItem['margin-top']}px`
            : barItem['margin-top'],
        marginBottom: typeof barItem['margin-bottom'] === 'number'
            ? `${barItem['margin-bottom']}px`
            : barItem['margin-bottom']
    };

    const startColor = barItem.startColor || '#FFA230';
    const endColor = barItem.endColor || '#A146B0';
    const duration = barItem.animationDuration || 2000;

    return (
        <div className="loading-bar-wrapper" style={barStyle}>
            <div className="loading-bar-top-row">
                {barItem.text && (
                    <div className="loading-bar-label">
                        {replaceTemplates(barItem.text)}
                    </div>
                )}
                <div className="loading-bar-right-side">
                    <div className="loading-bar-percentage">{percentage}%</div>
                    {isAnimating && percentage < 100 && (
                        <div className="loading-bar-spinner" style={{ display: 'inline-block' }} />
                    )}
                </div>
            </div>
            <div className="loading-bar-container">
                <div
                    className="loading-bar"
                    style={{
                        background: `linear-gradient(90deg, ${startColor}, ${endColor})`,
                        width: `${isCompleted ? 100 : barWidth}%`,
                        transition: isAnimating ? `width ${duration}ms ease-out` : 'none'
                    }}
                />
            </div>
        </div>
    );
};

const LoadingBarsElement = ({ element }) => {
    const { goToNextScreen } = useOnboarding();
    const [currentBarIndex, setCurrentBarIndex] = useState(-1);
    const style = applyStyles(element);

    useEffect(() => {
        // Start first bar after mount
        const timer = setTimeout(() => {
            setCurrentBarIndex(0);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    const handleBarComplete = (index) => {
        if (index < (element.items?.length || 0) - 1) {
            // Move to next bar
            setCurrentBarIndex(index + 1);
        } else {
            // All bars completed - auto-navigate to next screen
            setTimeout(() => {
                goToNextScreen(true);
            }, 500);
        }
    };

    if (!element.items || !Array.isArray(element.items)) {
        return null;
    }

    return (
        <div className="element-loading-bars" style={style}>
            {element.items.map((barItem, index) => (
                <LoadingBar
                    key={index}
                    barItem={barItem}
                    onComplete={() => handleBarComplete(index)}
                    isAnimating={index === currentBarIndex}
                    isCompleted={index < currentBarIndex}
                />
            ))}
        </div>
    );
};

export default LoadingBarsElement;

